// src/components/AdminDashboard.js
import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

const UserEditModal = ({ isOpen, onClose, user, onSave, setNotification }) => {
    const [formData, setFormData] = useState({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        position: user?.position || '',
        department: user?.department || '',
        role: user?.role || 'user'
    });

    // === NEU: Separate States für die Berechtigungen ===
    const [permissions, setPermissions] = useState({
        canManageUsers: user?.permissions?.canManageUsers || false,
        viewAllProjects: user?.permissions?.viewAllProjects || false,
    });
    const [allowedProjects, setAllowedProjects] = useState((user?.allowedProjects || []).join(', '));
    const [isSaving, setIsSaving] = useState(false);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePermissionChange = (e) => {
        const { name, checked } = e.target;
        setPermissions(prev => ({ ...prev, [name]: checked }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            // 1. Berechtigungen via Cloud Function speichern (Sicherheits-Regel!)
            // Das ist notwendig, weil normale User-Updates in firestore.rules oft das 'permissions' Feld blockieren
            const functions = getFunctions();
            const updatePermissionsFn = httpsCallable(functions, 'updateUserPermissions');
            
            const projectsArray = allowedProjects.split(',').map(s => s.trim()).filter(s => s !== '');

            // Wir rufen die Cloud Function auf, um die sensiblen Rechte zu setzen
            await updatePermissionsFn({
                targetUserId: user.id,
                permissions: permissions,
                allowedProjects: projectsArray
            });

            // 2. Stammdaten via Callback speichern (lokales Update oder Firestore direct)
            // Wir übergeben das komplette Objekt zurück
            await onSave({ 
                ...user, 
                ...formData,
                permissions, 
                allowedProjects: projectsArray 
            });

            if (setNotification) setNotification({ type: 'success', message: 'Benutzer & Rechte erfolgreich gespeichert.' });
            onClose();
        } catch (error) {
            console.error("Fehler beim Speichern:", error);
            if (setNotification) setNotification({ type: 'error', message: `Fehler beim Speichern: ${error.message}` });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-2xl w-full max-w-2xl relative max-h-[90vh] overflow-y-auto">
                <h2 className="text-2xl font-bold mb-6 dark:text-white">Benutzer bearbeiten</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Linke Spalte: Stammdaten */}
                    <div className="space-y-4">
                        <h3 className="font-bold text-gray-700 dark:text-gray-300 border-b pb-2">👤 Stammdaten</h3>
                        <div>
                            <label className="text-xs text-gray-500">Vorname</label>
                            <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white"/>
                        </div>
                        <div>
                            <label className="text-xs text-gray-500">Nachname</label>
                            <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white"/>
                        </div>
                        <div>
                            <label className="text-xs text-gray-500">Position</label>
                            <input type="text" name="position" value={formData.position} onChange={handleChange} className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white"/>
                        </div>
                        <div>
                            <label className="text-xs text-gray-500">Abteilung</label>
                            <input type="text" name="department" value={formData.department} onChange={handleChange} className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white"/>
                        </div>
                        <div>
                            <label className="text-xs text-gray-500">Haupt-Rolle</label>
                            <select name="role" value={formData.role} onChange={handleChange} className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white">
                                <option value="user">User (Standard)</option>
                                <option value="teamleader">Teamleiter</option>
                                <option value="admin">Admin (Vollzugriff)</option>
                            </select>
                        </div>
                    </div>

                    {/* Rechte Spalte: Berechtigungen */}
                    <div className="space-y-4">
                         <h3 className="font-bold text-gray-700 dark:text-gray-300 border-b pb-2">🔒 Zugriff & Rechte</h3>
                         
                         <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-3">
                             <div className="flex items-center justify-between">
                                <label className="text-sm font-medium dark:text-white cursor-pointer" htmlFor="chk-manage">Kann User verwalten</label>
                                <input 
                                    id="chk-manage"
                                    type="checkbox" 
                                    name="canManageUsers"
                                    checked={formData.role === 'admin' || permissions.canManageUsers} 
                                    disabled={formData.role === 'admin'} // Admins haben das immer
                                    onChange={handlePermissionChange}
                                    className="w-5 h-5 text-blue-600 rounded"
                                />
                             </div>
                             <p className="text-xs text-gray-500">Erlaubt Zugriff auf dieses Dashboard.</p>
                         </div>

                         <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-3">
                             <div className="flex items-center justify-between">
                                <label className="text-sm font-medium dark:text-white cursor-pointer" htmlFor="chk-gf">Sieht ALLE Projekte (GF)</label>
                                <input 
                                    id="chk-gf"
                                    type="checkbox" 
                                    name="viewAllProjects"
                                    checked={permissions.viewAllProjects} 
                                    onChange={handlePermissionChange}
                                    className="w-5 h-5 text-blue-600 rounded"
                                />
                             </div>
                             <p className="text-xs text-gray-500">Ignoriert Projekt-Filter (außer 'Geheim').</p>
                         </div>

                         <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-2">
                            <label className="block text-sm font-medium dark:text-white">Erlaubte Projekte</label>
                            <input 
                                type="text" 
                                value={allowedProjects}
                                onChange={(e) => setAllowedProjects(e.target.value)}
                                placeholder="z.B. Vertrieb, Marketing" 
                                disabled={permissions.viewAllProjects}
                                className="w-full p-2 border rounded dark:bg-gray-600 dark:text-white disabled:opacity-50 text-sm"
                            />
                            <p className="text-xs text-gray-500">Kommagetrennte Liste (Groß-/Kleinschreibung beachten).</p>
                         </div>
                    </div>
                </div>

                <div className="mt-8 flex justify-end space-x-3 pt-4 border-t dark:border-gray-700">
                    <button onClick={onClose} className="px-6 py-2 bg-gray-300 dark:bg-gray-600 dark:text-white rounded-lg font-medium hover:bg-gray-400 transition" disabled={isSaving}>Abbrechen</button>
                    <button onClick={handleSave} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center font-medium transition" disabled={isSaving}>
                        {isSaving ? 'Speichere...' : 'Speichern'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const AdminDashboard = ({ currentCompanyId, setNotification, registerUserCallable, companyUsers, onEditUser }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('user');
  const [editingUser, setEditingUser] = useState(null);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!firstName || !lastName) {
        setNotification({ message: 'Vor- und Nachname sind erforderlich.', type: 'error' });
        return;
    }
    
    try {
      await registerUserCallable({ 
        email, 
        password, 
        companyId: currentCompanyId, 
        role, 
        firstName, 
        lastName 
        // Hier könnten wir permissions auch schon beim Erstellen übergeben, wenn gewünscht
      });
      setNotification({ message: 'Benutzer erfolgreich registriert!', type: 'success' });
      setEmail('');
      setPassword('');
      setFirstName('');
      setLastName('');
      setRole('user');
    } catch (error) {
      console.error("Fehler bei der Benutzerregistrierung:", error);
      let userMessage = `Registrierung fehlgeschlagen. Fehler: ${error.message}`;
      setNotification({ message: userMessage, type: 'error' });
    }
  };

  const handleUserUpdate = async (updatedUserData) => {
      // Hier rufen wir die übergebene Funktion auf (die wahrscheinlich Firestore update macht)
      // Die Permissions wurden bereits im Modal via Cloud Function gespeichert.
      try {
          await onEditUser(updatedUserData);
          setEditingUser(null);
          // Kein setNotification hier nötig, das Modal hat es schon gemacht oder wir machen es hier:
          // setNotification({ message: 'Daten aktualisiert', type: 'success' });
      } catch (e) {
          console.error(e);
      }
  };

  return (
    <div className="p-4 sm:p-8 bg-white dark:bg-gray-800 rounded-2xl shadow-xl min-h-screen">
      <h2 className="text-3xl font-bold mb-8 text-gray-800 dark:text-white border-b pb-4">Admin-Dashboard</h2>
      
      {/* 1. Benutzer erstellen */}
      <div className="mb-10">
          <div className="p-6 bg-indigo-50 dark:bg-gray-900/50 rounded-xl border border-indigo-100 dark:border-gray-700">
            <h3 className="text-xl font-bold mb-4 flex items-center text-indigo-900 dark:text-indigo-300">
                <span className="mr-2">+</span> Neuen Benutzer anlegen
            </h3>
            <form onSubmit={handleRegister} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input type="text" id="new-firstName" name="firstName" placeholder="Vorname" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full p-3 border rounded-lg dark:bg-gray-800 dark:text-white" required />
              <input type="text" id="new-lastName" name="lastName" placeholder="Nachname" value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-full p-3 border rounded-lg dark:bg-gray-800 dark:text-white" required />
              <input type="email" id="new-email" name="email" placeholder="E-Mail" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-3 border rounded-lg dark:bg-gray-800 dark:text-white" required />
              <input type="password" id="new-password" name="password" placeholder="Passwort" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full p-3 border rounded-lg dark:bg-gray-800 dark:text-white" required />
              <select id="new-role" name="role" value={role} onChange={(e) => setRole(e.target.value)} className="w-full p-3 border rounded-lg dark:bg-gray-800 dark:text-white">
                <option value="user">User</option>
                <option value="teamleader">Teamleader</option>
                <option value="admin">Admin</option>
              </select>
              <button type="submit" className="md:col-span-2 px-6 py-3 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 transition shadow-lg mt-2">
                  Benutzer erstellen
              </button>
            </form>
          </div>
      </div>

      {/* 2. Benutzer Liste */}
      <div>
        <h3 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">Benutzer verwalten ({companyUsers.length})</h3>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden border dark:border-gray-700">
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {companyUsers.map(user => (
                    <div key={user.id} className="flex flex-col sm:flex-row justify-between items-center p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition duration-150">
                        <div className="flex items-center space-x-4 mb-2 sm:mb-0 w-full sm:w-auto">
                            <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 font-bold">
                                {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                            </div>
                            <div>
                                <p className="font-semibold text-gray-900 dark:text-white">{user.firstName} {user.lastName}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                            </div>
                            <div className="hidden md:block ml-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                                    {user.role || 'user'}
                                </span>
                                {user.permissions?.viewAllProjects && (
                                    <span className="ml-2 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">GF</span>
                                )}
                            </div>
                        </div>
                        <button 
                            onClick={() => setEditingUser(user)} 
                            className="w-full sm:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition text-gray-700 dark:text-gray-300"
                        >
                            Bearbeiten & Rechte
                        </button>
                    </div>
                ))}
            </div>
        </div>
      </div>

      {editingUser && (
        <UserEditModal 
          isOpen={!!editingUser}
          onClose={() => setEditingUser(null)}
          user={editingUser}
          onSave={handleUserUpdate}
          setNotification={setNotification}
        />
      )}
    </div>
  );
};

export default AdminDashboard;